import BaseApi from '@/api/Base/BaseApi'
import {
  CheckValidRequirementsPayload,
  CreateModelNodePayload,
  GetClassesResponse,
  GetModelDownloadListResponse,
  GetTabularResultItemsResponse,
  GetTrainItemsPayload,
  GetTrainResultResponse,
  GetTrainStatusResponse,
  ModelExportPayload,
  ModelNodeInfo,
  UpdateModelNodePayload,
  UpdateThresholdPayload,
  WeightScore
} from '@/api/ModelNode/types'
import { CommonListPayload } from '@/api/DatasetNode/types'
import { GetLabelingNodeItemsResponse } from '@/api/LabelingNode/types'
import {
  CommonValidationResult,
  GetValidationResultItemsResponse,
  VideoSummaryResult
} from '@/api/ValidationNode/types'
import { GetVideoResultPayload, GetVideoResultResponse, VideoHeatmapItem } from '@/api/InferenceNode/types'

const ROOT = '/model'

const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'asc',
  sortKey: 'datasetItemId',
}

export default class ModelNodeApi extends BaseApi {
  createModelNode(payload: CreateModelNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<ModelNodeInfo>(`/project/${projectId}/model/create`, rest)
  }

  getModelNodeDetail(modelNodeId: string) {
    return this.agent.get<ModelNodeInfo>(`${ROOT}/${modelNodeId}`)
  }

  updateModelNode(payload: UpdateModelNodePayload) {
    const { modelNodeId, name, ...rest } = payload
    return this.agent.put<ModelNodeInfo>(`${ROOT}/${modelNodeId}`, { ...rest, name: name?.trim() })
  }

  deleteModelNode(modelNodeId: string) {
    return this.agent.delete(`${ROOT}/${modelNodeId}/delete`)
  }

  startTrain(modelNodeId: string) {
    return this.agent.post<GetTrainResultResponse>(`${ROOT}/${modelNodeId}/train`)
  }

  getTrainStatus(modelNodeId: string) {
    return this.agent.get<GetTrainStatusResponse>(`${ROOT}/${modelNodeId}/train/status`)
  }

  getTrainResult(modelNodeId: string) {
    return this.agent.get(`${ROOT}/${modelNodeId}/train`)
  }

  getTrainInformation(modelNodeId: string) {
    return this.agent.get(`${ROOT}/${modelNodeId}/information`)
  }

  getTrainLoss(modelNodeId: string) {
    return this.agent.get(`${ROOT}/${modelNodeId}/train/loss-graph`)
  }

  checkValidRequirements(payload: CheckValidRequirementsPayload) {
    const { modelNodeId, ...rest } = payload
    return this.agent.post<ModelNodeInfo>(`${ROOT}/${modelNodeId}/training-check`, rest)
  }

  /** @deprecated */
  resetModelNode(modelNodeId: string) {
    return this.agent.put(`${ROOT}/${modelNodeId}/reset`)
  }

  exportModel(payload: ModelExportPayload) {
    const { modelNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/${modelNodeId}/export`, rest)
  }

  getModelDownloadList(projectId: string, options?: CommonListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    return this.agent.get<GetModelDownloadListResponse>(`${ROOT}/${projectId}/download/list`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getTrainItems(modelNodeId: string, options?: GetTrainItemsPayload) {
    const { itemsPerPage, page, sortDir, sortKey, classIds } = { ...defaultPaginationOptions, ...options }
    return this.agent.get<GetLabelingNodeItemsResponse>(`${ROOT}/${modelNodeId}/items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
        classIds,
      },
    })
  }

  getTrainResultSummary(modelNodeId: string) {
    return this.agent.get<CommonValidationResult>(`${ROOT}/${modelNodeId}/summary`)
  }

  getTrainResultItems(modelNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    return this.agent.get<GetValidationResultItemsResponse>(`${ROOT}/${modelNodeId}/result-items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getCheckpoints(modelNodeId: string) {
    return this.agent.get<WeightScore[]>(`${ROOT}/${modelNodeId}/checkpoints`)
  }

  getClasses(modelNodeId: string) {
    return this.agent.get<GetClassesResponse>(`${ROOT}/${modelNodeId}/classes`)
  }

  updateThreshold(payload: UpdateThresholdPayload) {
    const { modelNodeId, ...rest } = payload
    return this.agent.put<GetTrainStatusResponse>(`${ROOT}/${modelNodeId}/threshold`, rest)
  }

  /** @deprecated */
  getTabularResultItems(modelNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    return this.agent.get<GetTabularResultItemsResponse>(`${ROOT}/${modelNodeId}/tabular-result-items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getVideoSummary(modelNodeId: string) {
    return this.agent.get<VideoSummaryResult>(`${ROOT}/${modelNodeId}/video-summary`)
  }

  getVideoResult(payload: Omit<GetVideoResultPayload, 'inferenceNodeId'> & { modelNodeId: string }) {
    const { modelNodeId, page, numPerPage, sortKey, sortDir } = payload
    return this.agent.get<GetVideoResultResponse>(`${ROOT}/${modelNodeId}/video-result`, {
      params: {
        page,
        num_per_page: numPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getVideoHeatmap(payload: { modelNodeId: string; startMs: number; endMs: number }) {
    const { modelNodeId, startMs, endMs } = payload
    return this.agent.get<VideoHeatmapItem[]>(`${ROOT}/${modelNodeId}/video-heatmap`, {
      params: { start_ms: startMs, end_ms: endMs },
    })
  }

  getTabularResult(modelNodeId: string) {
    return this.agent.get<any>(`${ROOT}/${modelNodeId}/tabular-result`)
  }

  /** @deprecated */
  getWindowItems(modelNodeId: string, startValue: any, endValue: any) {
    return this.agent.get<any>(`${ROOT}/${modelNodeId}/window-items`, {
      params: {
        startValue,
        endValue,
      },
    })
  }
}
